import React, { useState, useRef, useEffect } from "react";
import { COLORS, CHAT_SUGGESTIONS } from "../data/profile";
import { CHAT_SYSTEM_PROMPT } from "../data/prompts";
import { sendChatMessage } from "../api";
import { useLang } from "../LanguageContext";

function TypingDots() {
  return (
    <div style={{ display: "flex", gap: 5, padding: 10 }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: COLORS.accent,
            animation: "pulse 1.2s infinite",
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </div>
  );
}

function ChatBubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        marginBottom: 10,
        animation: "slideInFromBottom 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      <div
        style={{
          maxWidth: "85%",
          padding: "11px 15px",
          borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
          background: isUser ? COLORS.dark : COLORS.warm,
          color: isUser ? "#F5F0EB" : COLORS.text,
          fontSize: 14,
          lineHeight: 1.65,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {text}
      </div>
    </div>
  );
}

export default function ChatTab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const { lang } = useLang();
  const suggestions = CHAT_SUGGESTIONS[lang] || CHAT_SUGGESTIONS.en;

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput("");

    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const reply = await sendChatMessage(CHAT_SYSTEM_PROMPT, newMessages);
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Connection error: ${error.message}. Please check that your API key is configured in .env` },
      ]);
    }

    setLoading(false);
  };

  return (
    <div>
      <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(20px, 5vw, 24px)", marginBottom: 6, color: COLORS.text }}>
        {lang === "en" ? "Ask AI About Me" : "Demandez à l'IA"}
      </h2>
      <p style={{ fontSize: 13, color: COLORS.muted, marginBottom: 18 }}>
        {lang === "en"
          ? "Trained on my actual work. Honest, including about what I do not know."
          : "Entraîné sur mon travail réel. Honnête, y compris sur ce que je ne sais pas."}
      </p>

      <div
        style={{
          background: "#fff",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          minHeight: 320,
          maxHeight: "calc(100dvh - 260px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: 16,
          WebkitOverflowScrolling: "touch",
        }}>
          {messages.length === 0 && (
            <div style={{ textAlign: "center", paddingTop: 24 }}>
              <p style={{ color: "#BBB", fontSize: 13, marginBottom: 14 }}>
                {lang === "en" ? "Try asking:" : "Essayez de demander :"}
              </p>
              {suggestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => { setInput(q); inputRef.current?.focus(); }}
                  style={{
                    display: "block",
                    margin: "7px auto",
                    padding: "8px 16px",
                    background: COLORS.warm,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: 16,
                    fontSize: 13,
                    color: "#555",
                    cursor: "pointer",
                    maxWidth: "90%",
                    lineHeight: 1.4,
                    transition: "background 0.15s",
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          )}
          {messages.map((m, i) => (
            <ChatBubble key={i} role={m.role} text={m.content} />
          ))}
          {loading && <TypingDots />}
          <div ref={scrollRef} />
        </div>

        <div style={{
          padding: "10px 12px",
          borderTop: "1px solid #F0EDE8",
          display: "flex",
          gap: 8,
          background: "#FAFAF8",
        }}>
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={lang === "en" ? "Ask about Chris..." : "Posez une question sur Chris..."}
            enterKeyHint="send"
            style={{
              flex: 1,
              padding: "10px 14px",
              border: `1px solid ${COLORS.border}`,
              borderRadius: 20,
              fontSize: 16,
              lineHeight: 1.4,
              background: "#fff",
            }}
          />
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            aria-label="Send"
            style={{
              padding: "10px 18px",
              background: loading || !input.trim() ? "#CCC" : COLORS.dark,
              color: "#F5F0EB",
              border: "none",
              borderRadius: 20,
              cursor: loading ? "wait" : "pointer",
              fontSize: 14,
              fontWeight: 500,
              minHeight: 44,
              transition: "background 0.2s, transform 0.1s",
            }}
          >
            {lang === "en" ? "Send" : "Envoyer"}
          </button>
        </div>
      </div>
    </div>
  );
}
